import React, { useEffect, useState } from "react";
import { Box, Button, Grid, IconButton, Tooltip, Stack } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import { useToast } from "../../services/utils";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import {
  deleteComparisonScoringConfig,
  reactivateComparisonScoringConfig,
} from "../../services/scoringService";
import { getRulesList } from "../../services/rulesService";
import { getFormattedDateTime } from "../../services/utils";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import Loader from "../../components/Molecules/Loader/Loader";
import {
  IconAudit,
  IconDeleteBlueSvg,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconEyeBase,
  IconEditBase,
  IconReactivateSvg,
} from "../../common/utils/icons";
import { paginatedResponseFormat } from "../../services/constants";
import useFetchPaginatedComparisonScoringConfigs from "../../hooks/useFetchPaginatedComparisonScoringConfigs";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useAuth } from "../../contexts/AuthContext";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import ButtonComponent from "../../components/Button.Component";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { formatEntityLabel } from "../../utils/scoringFormatters";

const FILTER_CONFIG = {
  DEFAULT_STATE: { rule_id: "", status_filter: "Active" },
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },
  FIELD_TYPES: {
    ID_FIELDS: ["rule_id"] as string[],
    STRING_FIELDS: ["status_filter"],
    DATE_FIELDS: [] as never[],
  },
} as const;

const ComparisonScoringList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission, isAdmin } = useAuth();
  const canEdit = isAdmin || hasPermission("comparison_scoring_config", "edit");
  const canAdd = isAdmin || hasPermission("comparison_scoring_config", "add");
  const canDelete =
    isAdmin || hasPermission("comparison_scoring_config", "delete");
  const { rowsPerPage } = useRowsPerPage();
  const { t } = useAppTranslation();
  const defaultPage = process.env.REACT_APP_DEFAULT_PAGE;
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [fileData, setFileData] = useState<any>(paginatedResponseFormat);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const parseUrlFilter = (field: string, raw: string): any => {
    if (FILTER_CONFIG.FIELD_TYPES.ID_FIELDS.includes(field)) {
      const n = Number(raw);
      return Number.isFinite(n) ? n : "";
    }
    return raw;
  };

  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const s: any = { ...FILTER_CONFIG.DEFAULT_STATE };
    FILTER_CONFIG.URL_FIELDS.forEach((f: string) => {
      const v = searchParams.get(f);
      if (v) s[f] = parseUrlFilter(f, v);
    });
    return s;
  });

  const [ruleMap, setRuleMap] = useState<Map<number, string>>(new Map());

  useEffect(() => {
    const fetchRules = async () => {
      try {
        const rules = await getRulesList();
        if (Array.isArray(rules)) {
          const map = new Map<number, string>();
          rules.forEach((r: any) => {
            if (r.id != null) map.set(r.id, r.name);
          });
          setRuleMap(map);
        }
      } catch {
        // silently fail — IDs will still be shown
      }
    };
    fetchRules();
  }, []);

  const [fetchedData, refetch] = useFetchPaginatedComparisonScoringConfigs({
    setIsLoading,
    page,
    pSize,
    searchFilterData,
  });

  useEffect(() => {
    const p = new URLSearchParams();
    // Preserve ScoringConfig's reserved tab params so filter writes don't
    // wipe parent tab state from the URL.
    ["tab", "runSub", "aggSub"].forEach((k) => {
      const v = searchParams.get(k);
      if (v !== null) p.set(k, v);
    });
    FILTER_CONFIG.URL_FIELDS.forEach((f) => {
      const v = searchFilterData[f];
      if (v) p.set(f, String(v));
    });
    if (searchParams.toString() !== p.toString())
      setSearchParams(p, { replace: true });
  }, [searchFilterData]);

  useEffect(() => {
    const s: any = {};
    let changed = false;
    FILTER_CONFIG.URL_FIELDS.forEach((f: string) => {
      const urlVal = searchParams.get(f);
      if (urlVal) {
        s[f] = parseUrlFilter(f, urlVal);
      } else if (f === "status_filter") {
        s[f] = FILTER_CONFIG.DEFAULT_STATE.status_filter;
      } else {
        s[f] =
          searchFilterData[f] ??
          FILTER_CONFIG.DEFAULT_STATE[
            f as keyof typeof FILTER_CONFIG.DEFAULT_STATE
          ];
      }
    });
    changed = Object.keys(s).some((k) => s[k] !== searchFilterData[k]);
    if (changed) setSearchFilterData(s);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
  }, [searchFilterData]);
  useEffect(() => {
    if (fetchedData?.items) setFileData(fetchedData);
  }, [fetchedData]);

  const handleDelete = (row: any) => {
    setItemToDelete(row);
    setDeleteConfirmOpen(true);
  };
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      setDeleteLoading(true);
      await deleteComparisonScoringConfig(itemToDelete.id);
      showToast(
        t("comparisonScoring.toast.deleted", {
          ruleId: itemToDelete.rule_id,
        }),
        "success"
      );
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
      refetch();
    } catch {
      showToast(t("comparisonScoring.toast.failedDelete"), "error");
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handleReactivate = async (row: any) => {
    setIsLoading(true);
    try {
      await reactivateComparisonScoringConfig(row.id);
      showToast(
        t("comparisonScoring.toast.reactivated", { id: row.id }),
        "success"
      );
      refetch();
    } catch {
      showToast(t("comparisonScoring.toast.failedReactivate"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
      renderCell: () => <></>,
    },
    {
      field: "id",
      headerName: t("comparisonScoring.list.columns.id"),
      minWidth: 70,
      flex: 0.5,
    },
    {
      field: "rule_id",
      headerName: t("comparisonScoring.list.columns.rule"),
      minWidth: 160,
      flex: 1.2,
      renderCell: (p: any) => {
        const id = p.value;
        const name = ruleMap.get(id);
        return (
          <LimitChractersWithTooltip value={formatEntityLabel(name, id)} />
        );
      },
    },
    {
      field: "blend_ratio",
      headerName: t("comparisonScoring.list.columns.blendRatio"),
      minWidth: 110,
      flex: 0.7,
    },
    {
      field: "category_weight_missing",
      headerName: t("comparisonScoring.list.columns.wtMissing"),
      minWidth: 110,
      flex: 0.7,
    },
    {
      field: "category_weight_mismatch",
      headerName: t("comparisonScoring.list.columns.wtMismatch"),
      minWidth: 110,
      flex: 0.7,
    },
    {
      field: "category_weight_validation",
      headerName: t("comparisonScoring.list.columns.wtValidation"),
      minWidth: 120,
      flex: 0.7,
    },
    {
      field: "missing_factor",
      headerName: t("comparisonScoring.list.columns.missingFactor"),
      minWidth: 120,
      flex: 0.7,
    },
    {
      field: "modified_by_user",
      headerName: t("comparisonScoring.list.columns.modifiedBy"),
      minWidth: 130,
      flex: 1,
      renderCell: (p: any) => (
        <LimitChractersWithTooltip value={p?.value?.username} />
      ),
    },
    {
      field: "updated_on",
      headerName: t("comparisonScoring.list.columns.updatedOn"),
      minWidth: 160,
      flex: 1,
      renderCell: (p: any) => getFormattedDateTime(p.value),
    },
    {
      field: "is_active",
      headerName: t("comparisonScoring.list.columns.status"),
      minWidth: 80,
      flex: 0.6,
      renderCell: (p: any) =>
        p.value ? (
          <Box title={t("comparisonScoring.list.tooltip.active")}>
            <IconCheckCircleIconGreenSvg width={21} height={21} />
          </Box>
        ) : (
          <Box title={t("comparisonScoring.list.tooltip.inactive")}>
            <IconCrossCircleIconSvg width={21} height={21} />
          </Box>
        ),
    },
    {
      field: "action",
      headerName: t("comparisonScoring.list.columns.action"),
      align: "right",
      headerAlign: "right",
      width: 140,
      renderCell: (p: any) => {
        const isActive = p?.row?.is_active === true;
        return (
          <>
            {isActive && (
              <>
                <Tooltip
                  title={t("comparisonScoring.list.tooltip.view")}
                  placement="top"
                  arrow
                >
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() =>
                      navigate(`/scoring/comparison/view/${p.row.id}`)
                    }
                  >
                    <IconEyeBase />
                  </IconButton>
                </Tooltip>
                {canEdit && (
                  <Tooltip
                    title={t("comparisonScoring.list.tooltip.edit")}
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(`/scoring/comparison/edit/${p.row.id}`)
                      }
                    >
                      <IconEditBase />
                    </IconButton>
                  </Tooltip>
                )}
                <DropdownMenu
                  dropdownMenuItems={[
                    <IconButton
                      key="audit"
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() =>
                        navigate(`/scoring/comparison/audit/${p.row.rule_id}`)
                      }
                    >
                      <IconAudit /> {t("comparisonScoring.list.dropdown.audit")}
                    </IconButton>,
                    ...(canDelete
                      ? [
                          <IconButton
                            key="delete"
                            className="datagrid-action-btn"
                            color="inherit"
                            onClick={() => handleDelete(p.row)}
                          >
                            <IconDeleteBlueSvg />{" "}
                            {t("comparisonScoring.list.dropdown.delete")}
                          </IconButton>,
                        ]
                      : []),
                  ]}
                />
              </>
            )}
            {!isActive && canEdit && (
              <Tooltip
                title={t("comparisonScoring.list.tooltip.reactivate")}
                placement="top"
                arrow
              >
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleReactivate(p.row)}
                >
                  <IconReactivateSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <Box>
      {(isLoading || deleteLoading) && (
        <Loader isLoading={isLoading || deleteLoading} />
      )}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("comparisonScoring.list.dialog.deleteTitle")}
        dailogDescription={t(
          "comparisonScoring.list.dialog.deleteDescription",
          {
            id: itemToDelete?.id,
          }
        )}
      />
      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              searchFilterData={searchFilterData}
              setSearchFilterData={setSearchFilterData}
              requiredFields={["rule_list", "status_filter"]}
              primaryFields={["rule_list", "status_filter"]}
              fieldRequirements={{
                queryNamePlaceholder: t("comparisonScoring.list.placeholder"),
                defaultStatusFilter: "Active",
                includeRuleId: true,
              }}
            />
          </Grid>
          {canAdd && (
            <Grid item display={"flex"} justifyContent={"flex-end"}>
              <Stack>
                <Box sx={{ display: "flex", columnGap: 2 }}>
                  <ButtonComponent
                    handelClickEvent={() => navigate("/scoring/comparison/add")}
                    className="btn-orange"
                  >
                    <AddSharpIcon sx={{ marginRight: "4px" }} />{" "}
                    {t("comparisonScoring.list.addButton")}
                  </ButtonComponent>
                </Box>
              </Stack>
            </Grid>
          )}
        </Grid>
      </Box>
      <FlexBetween>
        <DataTable
          dataColumns={columns || []}
          dataRows={fileData?.items || []}
          loading={isLoading}
          dataListTitle={t("comparisonScoring.list.dataListTitle")}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{ page: page - 1, pageSize: pSize }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={360}
        />
      </FlexBetween>
    </Box>
  );
};

export default ComparisonScoringList;
